import { axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import { SegmentExpenditureAndExecutionsModel } from '../../../_models/segment-expenditure-and-execution';

type SegmentExpenditureAndExecutionsType = z.infer<
  typeof SegmentExpenditureAndExecutionsModel
>;
type SegmentExpenditureAndExecutionType =
  SegmentExpenditureAndExecutionsType[number];

export const useSegmentExpenditureAndExecution = <
  U = SegmentExpenditureAndExecutionsType,
>({
  projectId,
  select,
  enabled = true,
}: {
  projectId: string;
  select?: (data: SegmentExpenditureAndExecutionsType) => U;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.SegmentExpenditureAndExecution(projectId),
    queryFn: async () => {
      const res = await axiosGet(
        `/api/projects/${projectId}/consecutive/segment-expenditure-and-execution/`,
      );
      return SegmentExpenditureAndExecutionsModel.parse(res);
    },
    select,
    enabled: !!projectId && enabled,
  });
};

export const useCreateSegmentExpenditureAndExecution = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      params,
    }: {
      params: Partial<SegmentExpenditureAndExecutionType> & {
        skipLock?: boolean;
      };
    }) => {
      const { projectId, ...rest } = params;
      const res = await axiosPost(
        `/api/projects/${projectId}/segment-expenditure-and-execution/`,
        rest,
      );
      await Promise.all([
        invalidate(queryKeyMap.project(projectId)),
        invalidate(queryKeyMap.SegmentExpenditureAndExecution()),
      ]);
      return res;
    },
  });
};

export const useUpdateSegmentExpenditureAndExecution = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      params,
    }: {
      params: Partial<SegmentExpenditureAndExecutionType> & {
        skipLock?: boolean;
      };
    }) => {
      const { projectId, id, ...rest } = params;
      const res = await axiosPatch(
        `/api/projects/${projectId}/segment-expenditure-and-execution/${id}/`,
        rest,
      );
      await Promise.all([
        invalidate(queryKeyMap.project(projectId)),
        invalidate(queryKeyMap.SegmentExpenditureAndExecution()),
      ]);
      return res;
    },
  });
};
